import {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TablePagination,
    Chip,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {CompanyAnnual} from '@/services/accountService/companyAnnualService';
import {annualPage} from '@/services/accountService/companyAnnualService';
import {RequestPage, ResponsePage} from '@/services/page.interface';
import AnnualDetail from '@/components/company-info/company/annual/AnnualDetail';

interface AnnualListProps {
    companyId: number;
    title?: string;
}

export default function AnnualList({companyId, title}: AnnualListProps) {
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [data, setData] = useState<ResponsePage<CompanyAnnual> | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedAnnualId, setSelectedAnnualId] = useState<number | null>(null);

    const requestParams: RequestPage = useMemo(
        () => ({pageNumber: page + 1, pageSize: rowsPerPage, pageType: 1}),
        [page, rowsPerPage]
    );

    const fetchData = async () => {
        if (!companyId) {
            return;
        }
        setLoading(true);
        try {
            const res = await annualPage(companyId, requestParams);
            setData(res);
            setError(null);
        } catch (e: any) {
            // Debug log for troubleshooting
            // eslint-disable-next-line no-console
            setError(e?.message || 'Failed to load annual list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Reset to first page when company changes
        setPage(0);
    }, [companyId]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, requestParams.pageNumber, requestParams.pageSize]);

    if (loading) return <CircularProgress sx={{display: 'block', mx: 'auto', my: 2}}/>;
    if (error) return <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12}}>{error}</Box>;

    const rows = data?.list || [];
    const total = data?.totalRow || 0;
    const showPagination = total > 5;

    return (
        <>
            <TableContainer component={Paper}>
                <Table
                    size="small"
                    sx={{
                        // Align content-row height with FsAdjustment (BS/PL leaf rows)
                        '& .MuiTableCell-root': {
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .MuiTableBody-root .MuiTableRow-root': {
                            height: 36,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow sx={{background: theme.palette.action.hover}}>
                            <TableCell sx={{fontWeight: 700}}>Assessment Year</TableCell>
                            <TableCell sx={{fontWeight: 700}}>Period</TableCell>
                            <TableCell sx={{fontWeight: 700}}>Months</TableCell>
                            <TableCell sx={{fontWeight: 700}}>Assessable Amount</TableCell>
                            <TableCell sx={{fontWeight: 700}}>Dividend Paid</TableCell>
                            <TableCell sx={{fontWeight: 700}}>Currency</TableCell>
                            <TableCell sx={{fontWeight: 700}}>Two-tier Tax</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No data
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((r, idx) => (
                            <TableRow
                                key={r.annualId ?? `row-${idx}`}
                                hover
                                sx={{cursor: 'pointer'}}
                                onClick={() => {
                                    if (r.annualId == null) return;
                                    setSelectedAnnualId(r.annualId);
                                    setDetailOpen(true);
                                }}
                            >
                                <TableCell>{r.yearOfAssessment || '-'}</TableCell>
                                <TableCell>{`${r.periodFrom || '-'} ~ ${r.periodTo || '-'}`}</TableCell>
                                <TableCell>{r.monthsInPeriod ?? '-'}</TableCell>
                                <TableCell>{r.assessableAmount ?? '-'}</TableCell>
                                <TableCell>{r.dividendPaid ?? '-'}</TableCell>
                                <TableCell>{r.dividendCurrency || '-'}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1}>
                                        <Chip size="small" label={`This: ${r.twoTierTaxThisYear ?? 0}`} color="primary"
                                              variant="outlined"/>
                                        <Chip size="small" label={`Last: ${r.twoTierTaxLastYear ?? 0}`}
                                              color="secondary" variant="outlined"/>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {showPagination && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[5, 10, 20]}
                        SelectProps={{
                            variant: 'outlined',
                            size: 'small',
                        }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                m: 0,
                                fontSize: 12
                            },
                            '& .MuiTablePagination-actions .MuiIconButton-root': {p: 0.25},
                            minHeight: 32,
                            px: 1,
                        }}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                )}
            </TableContainer>
            <AnnualDetail
                open={detailOpen}
                annualId={selectedAnnualId ?? undefined}
                onClose={() => {
                    setDetailOpen(false);
                    setSelectedAnnualId(null);
                }}
            />
        </>
    );
}
